import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Avatar, Box, Chip, IconButton, ListItem, Typography } from '@mui/material'
import AlertLogListItem from '../alertLogListItem/AlertLogListItem'
import { Close } from '@mui/icons-material'
import { useStyles } from './styles'
import { useStyles as useStylesLog } from '../alertLogListItem/styles'
import { useDispatch } from 'react-redux'
import { addFiredAlertLog, removeAllFiredAlerts } from '../../../reducer/alertLogs.reducer'
import { WebSocketResponseMessage } from '../../../proto/generated/websocket'
import { WebSocketContext } from '../../webSocketConnection/WebSocket'
import { useSelector } from 'react-redux'
import { selectFiredAlerts } from '../../../selectors/alerts'
import OrderAlertItem from '../alertLogListItem/OrderAlertItem'
import { UserAlertLog } from '../../../proto/generated/nexus'
import { Order } from '../../../proto/generated/order'
import { removeAllOrderFiredAlerts } from '../../../reducer/orders.reducer'
import { updateUserAlert } from '../../../reducer/alerts.reducer.ts'

const AlertsToastGroup = () => {
    const { classes } = useStyles()
    const { classes: classes2 } = useStylesLog()
    const dispatch = useDispatch()
    const ws = useContext(WebSocketContext)
    const [expanded, setExpanded] = useState(false)
    const { firedAlerts: firedAlertsOnly, orderAlerts, recentFiredAlerts } = useSelector(selectFiredAlerts)
    const firedAlerts = useMemo(()=>{
        return [...(firedAlertsOnly.filter((v: UserAlertLog) => recentFiredAlerts.includes(v.id))??[]), ...(orderAlerts??[])]
    }, [orderAlerts, firedAlertsOnly, recentFiredAlerts])
    const sortedFiredAlerts = useMemo(() => firedAlerts?.sort((a: any, b: any) => (a?.triggeredTime ?? a?.exchangeTimestamp) - (b.triggeredTime ?? b.exchangeTimestamp)), [firedAlerts])
    const containerRef = useRef<HTMLDivElement>(undefined)
    const successSoundRef = useRef<HTMLAudioElement>(null)
    const [boxRef, setBoxRef] = useState<HTMLDivElement | undefined>(undefined)

    const onWebSocketMessageReceived = useCallback((data: Uint8Array) => {
        const responseData = WebSocketResponseMessage.decode(data)
        try {
            if (responseData.userAlertLog && responseData.userAlertLog?.alert) {
                if (successSoundRef.current) {
                    if (!successSoundRef.current.paused) {
                        successSoundRef.current.pause()
                        successSoundRef.current.currentTime = 0
                    }
                    successSoundRef.current.play()
                }
                dispatch(updateUserAlert(responseData.userAlertLog.alert))
                dispatch(addFiredAlertLog(responseData.userAlertLog))
            }
        } catch (e) {
            console.error(e, 'sound play error')
        }
    }, [])

    useEffect(() => {
        ws!.addListener(onWebSocketMessageReceived)
        return () => {
            if (ws !== null) {
                ws.removeListener(onWebSocketMessageReceived)
            }
        }
    }, [onWebSocketMessageReceived, ws])

    const handleExpansionChange = useCallback(() => {
        setExpanded(!expanded)
    }, [expanded])

    const handleCloseAll = useCallback(() => {
        dispatch(removeAllFiredAlerts())
        dispatch(removeAllOrderFiredAlerts())
    }, [])

    const controls = useMemo(() => {
        const chipLabel = (() => {
            return sortedFiredAlerts ? (
                <Box className={classes.chipLabel}>
                    <Typography component={'span'} fontSize={'small'}>
                        {expanded ? 'Show less' : 'Show more'}
                    </Typography>
                    <Avatar className={classes.chipAvatar}>{sortedFiredAlerts.length > 99 ? '99+' : sortedFiredAlerts.length}</Avatar>
                </Box>
            ) : null
        })()
        return sortedFiredAlerts?.length > 1 ? (
            <Box className={classes.subHeader}>
                <Box className={classes.chipContainer}>
                    <Chip label={chipLabel} size="small" className={classes.chip} onClick={handleExpansionChange} />
                    <IconButton size="small" className={classes.close} onClick={handleCloseAll}>
                        <Close sx={{ fontSize: '18px' }} />
                    </IconButton>
                </Box>
            </Box>
        ) : null
    }, [expanded, handleExpansionChange, sortedFiredAlerts, classes])

    const firedAlertsContainer = useCallback(() => {
        if (expanded) {
            return firedAlerts?.map((fAlert) => {
                if ((fAlert as any)?.orderAlertId) {
                    return <OrderAlertItem order={fAlert as Order & { orderAlertId: string }} />
                }
                return <AlertLogListItem log={fAlert as UserAlertLog} alertType="fired" />
            })
        }
        return firedAlerts ? (
            <>
                <Box className={classes.itemsContainer}>
                    <Box className={classes.item1} ref={(ref: HTMLDivElement)=>setBoxRef(ref)}>
                        {
                            (firedAlerts[firedAlerts.length - 1] as any)?.orderAlertId ?
                                <OrderAlertItem order={firedAlerts[firedAlerts.length - 1] as Order & { orderAlertId: string }} /> :
                                <AlertLogListItem log={firedAlerts[firedAlerts.length - 1] as UserAlertLog} alertType="fired" />
                        }
                    </Box>
                    {firedAlerts.length > 1 && (
                        <Box className={classes.item2} sx={{ height: boxRef?.clientHeight, width: boxRef?.clientWidth*0.9}}>
                            <ListItem
                                className={classes2.firedContainer}
                                sx={{
                                    height: boxRef?.clientHeight, width: boxRef?.clientWidth*0.9
                                }}
                            ></ListItem>
                        </Box>
                    )}
                    {firedAlerts.length > 2 && (
                        <Box className={classes.item3}>
                            <ListItem
                                className={classes2.firedContainer}
                                sx={{
                                    height: boxRef?.clientHeight, width: boxRef?.clientWidth*0.8
                                }}
                            ></ListItem>
                        </Box>
                    )}
                </Box>
            </>
        ) : null
    }, [expanded, firedAlerts, classes])

    const showToastGroup = useMemo(() => firedAlerts?.length > 0, [firedAlerts])

    useEffect(() => {
        if (expanded && containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight
        }
    }, [expanded])

    return <>
        <audio ref={successSoundRef} src={'/sound/success.mp3'} />
        {showToastGroup ? (
            <Box ref={containerRef} className={classes.container}>
                {controls}
                {firedAlertsContainer()}
            </Box>
        ) : null}
    </>
}

export default memo(AlertsToastGroup)
